import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import styled from 'styled-components';
import { Translate } from 'react-localize-redux';

/**
 * SHER-223 Phase 2: render the chronological correction trail for a single
 * submission. One card per addendum, with author + timestamp header. Always
 * rendered (including printMode), since addenda are clinical content that
 * MUST appear in PDFs and printed records.
 */

export interface Addendum {
    id: number;
    body: string;
    createdAt: string | Date;
    author?: { uuid?: string; name?: string; surnames?: string } | null;
}

interface Props {
    addenda: Addendum[];
}

const AddendumCard = styled(Card)`
    margin-top: 0.75rem;
    border-left: 3px solid #f0a500;
    background-color: #fffbf2;
`;

const Header = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 0.5rem;
`;

function formatTimestamp(value: string | Date): string {
    try {
        const d = typeof value === 'string' ? new Date(value) : value;
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
    } catch {
        return String(value);
    }
}

export const AddendaList: React.FC<Props> = ({ addenda }) => {
    if (!addenda || addenda.length === 0) {
        return null;
    }

    return (
        <Box data-testid="addenda-list" style={{ marginTop: '1rem' }}>
            <Typography variant="subtitle2" color="textPrimary" style={{ fontWeight: 600 }}>
                <Translate id="hospital.addendum-section-title" />
            </Typography>
            {addenda.map((addendum) => {
                const authorName = addendum.author
                    ? `${addendum.author.name || ''} ${addendum.author.surnames || ''}`.trim()
                    : '—';
                return (
                    <AddendumCard key={addendum.id} data-testid={`addendum-${addendum.id}`}>
                        <CardContent>
                            <Header>
                                <Typography variant="caption" color="textSecondary">
                                    <Translate id="hospital.addendum-author-label" />: <strong>{authorName || '—'}</strong>
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    <Translate id="hospital.addendum-timestamp-label" />: {formatTimestamp(addendum.createdAt)}
                                </Typography>
                            </Header>
                            <Typography variant="body2" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
                                {addendum.body}
                            </Typography>
                        </CardContent>
                    </AddendumCard>
                );
            })}
        </Box>
    );
};

export default AddendaList;
